import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonBase, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import Styles from '@/assets/js/Styles';
import { DMArtifact, DMMediaModel } from '@/declarations/models/DMMediaModel';
import { GenericMedia } from '@/declarations/GenericMedia';
import { Api } from '@/services/Api';
import { Settings } from '@/Settings';
import Container from '../../Container';
import Image from '../../Image';
import Loader from '../../Loader';

export interface DMImagePickerProps {
  /** The DM object to pick images from. The dialog is open while it is set */
  item: GenericMedia<DMMediaModel> | null;
  /** Images from this object that are already selected */
  selectedItems: Array<GenericMedia<DMMediaModel>>;
  multiSelect: boolean;
  onClose: () => void;
  /** All images selected from the object, replacing the ones selected before */
  onConfirm: (item: GenericMedia<DMMediaModel>, images: Array<GenericMedia<DMMediaModel>>) => void;
}

/** Each image gets its own id, so several images from the same object can be selected */
export const getDMImageItemId = (item: GenericMedia<DMMediaModel>, mediaId: string) =>
  `${item.source?.['artifact.uuid'] ?? item.id}:${mediaId}`;

/**
 * Makes a finder item for one of the object's images. The image takes the place of the object's main image,
 * so the item is saved the same way as when the main image is picked.
 */
const toImageItem = (item: GenericMedia<DMMediaModel>, mediaId: string, index: number) =>
  ({
    ...item,
    id: getDMImageItemId(item, mediaId),
    url: `${Settings.DM_HOST}/image/${mediaId}`,
    source: {
      ...item.source,
      'artifact.defaultMediaIdentifier': mediaId,
      'artifact.defaultPictureIndex': index,
    },
  } as GenericMedia<DMMediaModel>);

export const DMImagePicker: FC<DMImagePickerProps> = ({ item, selectedItems, multiSelect, onClose, onConfirm }) => {
  const { t } = useTranslation('components');
  const [artifact, setArtifact] = useState<DMArtifact | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedMediaIds, setSelectedMediaIds] = useState<Array<string>>([]);

  const uniqueId = item?.source?.['artifact.uniqueId'];

  useEffect(() => {
    if (!item) return;
    setSelectedMediaIds(
      selectedItems
        .filter((i) => i.source?.['artifact.uuid'] === item.source?.['artifact.uuid'])
        .map((i) => i.source?.['artifact.defaultMediaIdentifier'] ?? '')
        .filter(Boolean),
    );
    // Only when another object is opened, so the user's picks aren't reset while the dialog is open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  useEffect(() => {
    setArtifact(null);
    if (!uniqueId) return;
    let unmounted = false;
    setIsLoading(true);
    const ctx = Api.getDMArtifact(uniqueId);
    ctx
      .fetchDirect(null)
      .then((res) => !unmounted && setArtifact(res))
      .finally(() => !unmounted && setIsLoading(false));
    return () => {
      unmounted = true;
      ctx.abort();
    };
  }, [uniqueId]);

  const images = (artifact?.images ?? []).filter((image) => !!image.mediaId);

  const toggleImage = (mediaId: string) => {
    setSelectedMediaIds((prev) => {
      if (prev.includes(mediaId)) return prev.filter((id) => id !== mediaId);
      return multiSelect ? [...prev, mediaId] : [mediaId];
    });
  };

  const handleConfirm = () => {
    if (!item) return;
    // Keep the object's image order
    const picked = images
      .map((image, index) => ({ mediaId: image.mediaId as string, index }))
      .filter(({ mediaId }) => selectedMediaIds.includes(mediaId))
      .map(({ mediaId, index }) => toImageItem(item, mediaId, index));
    onConfirm(item, picked);
  };

  return (
    <Dialog open={!!item} onClose={onClose} maxWidth='lg' fullWidth>
      <DialogTitle>{item?.title}</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2 }}>
          {multiSelect ? t('DMImagePicker.InfoTextMultiple') : t('DMImagePicker.InfoTextSingle')}
        </Typography>
        {isLoading && <Loader />}
        {!isLoading && !images.length && (
          <Typography sx={{ fontStyle: 'italic' }}>{t('DMImagePicker.NoImages')}</Typography>
        )}
        {!isLoading && !!images.length && (
          <Container left wrap gap={1}>
            {images.map((image, index) => {
              const mediaId = image.mediaId as string;
              const isSelected = selectedMediaIds.includes(mediaId);
              return (
                <ButtonBase
                  key={mediaId}
                  onClick={() => toggleImage(mediaId)}
                  aria-pressed={isSelected}
                  aria-label={t('DMImagePicker.ImageLabel', { number: index + 1 })}
                  sx={{
                    width: '200px',
                    height: '200px',
                    backgroundColor: Styles.Colors.MEDIUM_GREY,
                    border: `4px solid ${isSelected ? 'green' : 'transparent'}`,
                    borderRadius: Styles.Dimensions.RADIUS_ROUNDNESS_DEFAULT,
                  }}>
                  <Image
                    src={`${Settings.DM_HOST}/image/${mediaId}?&dimension=250x250`}
                    alt={image.photographer ?? ''}
                    loading='lazy'
                    sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  />
                </ButtonBase>
              );
            })}
          </Container>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('DMImagePicker.Cancel')}</Button>
        <Button variant='contained' onClick={handleConfirm} disabled={isLoading || !artifact}>
          {t('DMImagePicker.Confirm', { count: selectedMediaIds.length })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DMImagePicker;
